// Image loading and caching utilities

/** @type {Map<string, HTMLImageElement>} */
const imageCache = new Map();

/** @type {Map<string, Promise<HTMLImageElement>>} */
const pendingLoads = new Map();

/**
 * Returns true while the image at `url` is being fetched for the first
 * time (i.e. `loadImage` has been called at least once but the image
 * has neither finished loading nor errored). Used by callers that want
 * to defer rendering an "image-missing" placeholder so it doesn't flash
 * during the initial load on a game refresh.
 * @param {string} url
 * @returns {boolean}
 */
export function isImageLoading(url) {
  return !!url && pendingLoads.has(url);
}

/**
 * Kick off a load for `url` and resolve when the image finishes loading
 * (or has already loaded). Resolves even on error so a single broken
 * image doesn't fail the whole preload batch — the renderer's existing
 * fallback path handles missing images at draw time.
 * @param {string} url
 * @returns {Promise<void>}
 */
export function preloadImage(url) {
  if (!url) return Promise.resolve();
  // Trigger the load if it hasn't started, then await its promise. If
  // the image is already cached and complete, loadImage returns it and
  // there's no pending promise to await.
  loadImage(url);
  const pending = pendingLoads.get(url);
  if (!pending) return Promise.resolve();
  return pending.then(
    () => undefined,
    () => undefined,
  );
}

/**
 * Preload every URL in `urls` in parallel and resolve once all have
 * loaded (or errored). A timeout cap prevents a single slow / hung
 * request from blocking the spinner indefinitely; the render loop will
 * pick up any late arrivals on subsequent frames.
 * @param {Iterable<string>} urls
 * @param {number} [timeoutMs=8000]
 * @returns {Promise<void>}
 */
export function preloadImages(urls, timeoutMs = 8000) {
  const list = [...urls].filter(Boolean);
  if (list.length === 0) return Promise.resolve();
  const all = Promise.all(list.map((u) => preloadImage(u))).then(() => undefined);
  const timeout = new Promise((resolve) => setTimeout(resolve, timeoutMs));
  return Promise.race([all, timeout]);
}

/**
 * Load an image and cache it
 * @param {string} url - Image URL
 * @returns {HTMLImageElement|null} Image element (null if not yet loaded)
 */
export function loadImage(url) {
  // Return cached image if available
  if (imageCache.has(url)) {
    const img = /** @type {HTMLImageElement} */ (imageCache.get(url));
    return img?.complete && img.naturalWidth > 0 ? img : null;
  }

  // Start loading if not already pending
  if (!pendingLoads.has(url)) {
    const img = new Image();

    const loadPromise = new Promise((resolve, reject) => {
      img.onload = () => {
        imageCache.set(url, img);
        pendingLoads.delete(url);
        resolve(img);
      };
      img.onerror = () => {
        pendingLoads.delete(url);
        reject(new Error(`Failed to load image: ${url}`));
      };
    });

    pendingLoads.set(url, loadPromise);
    img.src = url;
    imageCache.set(url, img);
  }

  return null;
}

/**
 * Skull icon for defeated tokens, served from the raster pipeline
 * (scripts/generate-icons.mjs → public/assets/icons/skull-white.png).
 * Returns null while the PNG is in flight; the renderer's
 * `!img.complete` guard skips drawing for one frame.
 *
 * @returns {HTMLImageElement|null}
 */
export function createSkullIcon() {
  return loadImage('/assets/icons/skull-white.png');
}

/**
 * Generic flame icon (lucide `flame`) used as the NPC light fallback
 * when a placement has `givesLight=true` but no specific slug. Served
 * from the raster pipeline.
 *
 * @returns {HTMLImageElement|null}
 */
export function createFlameIcon() {
  return loadImage('/assets/icons/flame-white.png');
}

/** @type {Record<string, {png: string, bgColor: string}>} */
const CONDITION_ICONS = {
  incapacitated: { png: '/assets/icons/incapacitated-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
  unconscious: { png: '/assets/icons/unconscious-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
  poisoned: { png: '/assets/icons/poisoned-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
  stunned: { png: '/assets/icons/stunned-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
  prone: { png: '/assets/icons/prone-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
  slowed: { png: '/assets/icons/slowed-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
  invisible: { png: '/assets/icons/invisible-white.png', bgColor: 'rgba(0, 0, 0, 0.85)' },
};

/**
 * Create condition icon. Every condition is served via the raster
 * pipeline (scripts/generate-icons.mjs) and cached through the
 * standard imageCache. Returns null while the PNG is in flight; the
 * renderer's `!img.complete` guard skips drawing for one frame and
 * picks the cached image up on the next.
 *
 * @param {string} condition - Condition name
 * @returns {HTMLImageElement|null}
 */
export function createConditionIcon(condition) {
  const iconConfig = CONDITION_ICONS[condition];
  if (!iconConfig || !iconConfig.png) return null;
  return loadImage(iconConfig.png);
}

/**
 * Get background color for condition
 * @param {string} condition - Condition name
 * @returns {string}
 */
export function getConditionBgColor(condition) {
  return CONDITION_ICONS[condition]?.bgColor || 'rgba(0, 0, 0, 0.8)';
}

/** @type {Record<string, string>} */
const LIGHT_SOURCE_PNG_URLS = {
  torch: '/assets/icons/torch-white.png',
  lantern: '/assets/icons/lantern-white.png',
  candle: '/assets/icons/candle-white.png',
};

/**
 * Light-source overlay icon (#371). The raster pipeline
 * (scripts/generate-icons.mjs) pre-rasterizes each light source SVG
 * to PNG at 128px in orange (#ff8800). This loads from the standard
 * imageCache so canvas draws don't pay the SVG rasterization cost
 * at small sizes. Returns null while the PNG is in flight; the
 * renderer's `!img.complete` guard skips drawing for one frame and
 * picks the cached image up on the next. Falls back to the existing
 * inline flame SVG for unknown slugs so NPCs without a kind hint
 * still get a glyph.
 *
 * @param {string} slug - one of "torch", "lantern", "candle"
 * @returns {HTMLImageElement|null}
 */
export function createLightSourceIcon(slug) {
  const url = LIGHT_SOURCE_PNG_URLS[slug];
  if (!url) return createFlameIcon();
  return loadImage(url);
}

/**
 * Pick the light-source icon slug for a PC's inventory by matching item
 * names — mirrors the server-side `iconForLightSourceTarget` in
 * compute_actions.go so the action menu and the token overlay agree on
 * which glyph belongs to which item.
 * @param {string} itemName
 * @returns {string} slug ("torch", "lantern", "candle") or "" when unrecognized
 */
export function lightSourceSlugForItemName(itemName) {
  const name = (itemName || '').trim().toLowerCase();
  if (name === 'torch') return 'torch';
  if (name === 'candle') return 'candle';
  if (name === 'lantern' || name === 'hooded lantern' || name === 'bullseye lantern' || name === 'lamp') {
    return 'lantern';
  }
  return '';
}

/**
 * Overlay icon priority buckets (#371). Lower number = higher priority
 * = renders closer to the top-right corner. The track caps at 4 visible
 * slots; if more apply, the 4th becomes a +N pill.
 *
 * Priority order (rightmost first):
 *  1. Defeated (skull)
 *  2. Incapacitating  — affects the actor's ability to act at all
 *  3. Movement        — restrained / prone / grappled / slowed
 *  4. Sense           — blinded / deafened
 *  5. Save-affecting  — frightened / charmed / poisoned / exhausted
 *  6. Light source    — informational, lowest
 */
export const OVERLAY_PRIORITY = {
  defeated: 0,
  incapacitating: 10,
  movement: 20,
  sense: 30,
  save: 40,
  light: 50,
};

/** @type {Record<string, number>} */
const CONDITION_PRIORITY = {
  // Incapacitating — these zero out actions outright.
  incapacitated: OVERLAY_PRIORITY.incapacitating,
  unconscious: OVERLAY_PRIORITY.incapacitating,
  paralyzed: OVERLAY_PRIORITY.incapacitating,
  stunned: OVERLAY_PRIORITY.incapacitating,
  petrified: OVERLAY_PRIORITY.incapacitating,
  // Movement-affecting.
  prone: OVERLAY_PRIORITY.movement,
  restrained: OVERLAY_PRIORITY.movement,
  grappled: OVERLAY_PRIORITY.movement,
  slowed: OVERLAY_PRIORITY.movement,
  // Sense-affecting.
  blinded: OVERLAY_PRIORITY.sense,
  deafened: OVERLAY_PRIORITY.sense,
  invisible: OVERLAY_PRIORITY.sense,
  // Save / damage modifying.
  frightened: OVERLAY_PRIORITY.save,
  charmed: OVERLAY_PRIORITY.save,
  poisoned: OVERLAY_PRIORITY.save,
  exhausted: OVERLAY_PRIORITY.save,
};

/**
 * Get the priority bucket for a condition slug. Unknown conditions
 * fall into the save bucket (the most "miscellaneous" category) so a
 * new server-side condition isn't dropped from the track entirely
 * before the registry catches up.
 * @param {string} condition
 * @returns {number}
 */
export function getConditionPriority(condition) {
  if (condition in CONDITION_PRIORITY) return CONDITION_PRIORITY[condition];
  return OVERLAY_PRIORITY.save;
}
